import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../core/services/user.service';
import { AuthResponse } from '../../core/interfaces/api-response';

@Component({
  selector: 'app-verify-2fa',
  templateUrl: './verify-2fa.component.html',
  styleUrl: './verify-2fa.component.scss',
  standalone: false,
})
export class Verify2FAComponent implements OnInit, AfterViewInit {
  verificationDigits: string[] = ['', '', '', '', '', ''];
  recoveryCode = '';
  isRecoveryMode = false;
  isVerifying = false;
  error = '';
  returnUrl = '/dashboard';
  rememberMe = false;
  rememberMePending = false;

  private tempToken = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.tempToken = this.userService.getTempToken() || '';

    // No temp token means user navigated here directly — redirect to login
    if (!this.tempToken) {
      this.router.navigate(['/account/login']);
      return;
    }

    // NEW: Check if Remember Me was requested during login
    this.rememberMePending = sessionStorage.getItem('remember_me_pending') === 'true';
    if (this.rememberMePending) {
      this.rememberMe = true;  // Pre-check the Remember Me checkbox
    }

    this.route.queryParams.subscribe(params => {
      this.returnUrl = params['returnUrl'] || '/dashboard';
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const firstInput = document.getElementById('verify-digit-0') as HTMLInputElement;
      firstInput?.focus();
    }, 100);
  }

  /**
   * Toggles between TOTP code input and recovery code input.
   */
  toggleRecoveryMode(): void {
    this.isRecoveryMode = !this.isRecoveryMode;
    this.error = '';
    this.recoveryCode = '';
    this.verificationDigits = ['', '', '', '', '', ''];
  }

  /**
   * Handles input in a TOTP digit field.
   * Auto-advances focus to the next field.
   */
  onDigitInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow single digit
    if (value.length > 1) {
      input.value = value.slice(-1);
      this.verificationDigits[index] = value.slice(-1);
    } else {
      this.verificationDigits[index] = value;
    }

    if (this.error) {
      this.error = '';
    }

    // Auto-advance to next field
    if (value && index < 5) {
      const nextInput = document.getElementById(`verify-digit-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }

    // Auto-submit when all digits are filled
    if (this.isCodeComplete()) {
      this.onSubmit();
    }
  }

  /**
   * Handles keydown for digit field navigation (backspace, arrow keys).
   */
  onDigitKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.verificationDigits[index] && index > 0) {
      const prevInput = document.getElementById(`verify-digit-${index - 1}`) as HTMLInputElement;
      prevInput?.focus();
      event.preventDefault();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.getElementById(`verify-digit-${index - 1}`) as HTMLInputElement;
      prevInput?.focus();
    }

    if (event.key === 'ArrowRight' && index < 5) {
      const nextInput = document.getElementById(`verify-digit-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  /**
   * Handles paste event to distribute digits across fields.
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    if (digits.length === 0) {
      return;
    }

    for (let i = 0; i < 6; i++) {
      this.verificationDigits[i] = digits[i] || '';
      const input = document.getElementById(`verify-digit-${i}`) as HTMLInputElement;
      if (input) {
        input.value = this.verificationDigits[i];
      }
    }

    // Focus last filled digit
    const lastFilledIndex = Math.min(digits.length, 6) - 1;
    const lastInput = document.getElementById(`verify-digit-${lastFilledIndex}`) as HTMLInputElement;
    lastInput?.focus();

    if (this.isCodeComplete()) {
      this.onSubmit();
    }
  }

  /**
   * Checks if all 6 TOTP digits are entered.
   */
  isCodeComplete(): boolean {
    return this.verificationDigits.every(digit => digit.length === 1);
  }

  /**
   * Submits the verification code (TOTP or recovery) with the temp_token.
   * NEW: Includes Remember Me option
   */
  async onSubmit(): Promise<void> {
    if (this.isVerifying) {
      return;
    }

    const code = this.isRecoveryMode
      ? this.recoveryCode.trim()
      : this.verificationDigits.join('');

    if (!code) {
      return;
    }

    // TOTP codes must be 6 digits
    if (!this.isRecoveryMode && code.length !== 6) {
      return;
    }

    this.isVerifying = true;
    this.error = '';

    try {
      // NEW: Call verify2FA with Remember Me option
      const verifyObservable = await this.userService.verify2FA(
        this.tempToken,
        code,
        this.isRecoveryMode,
        this.rememberMe  // Pass Remember Me preference
      );

      verifyObservable.subscribe({
        next: (result) => this.onVerifyResult(result),
        error: (error: unknown) => this.onVerifyFailed(error),
      });
    } catch (error) {
      this.onVerifyFailed(error);
    }
  }

  /** The backend answered: a token means we are in, anything else is a failed code. */
  private onVerifyResult(result: unknown): void {
    this.isVerifying = false;

    const token = this.userService.getToken();
    if (token) {
      this.clearSavedHotelForHotelUser();
      this.router.navigateByUrl(this.returnUrl || '/dashboard');
      return;
    }

    // No token means the 2FA verification itself failed on the backend.
    const authResult = result as AuthResponse;
    if (authResult?.data?.requires_2fa) {
      this.error = this.translate.instant('AUTH.VERIFY_2FA.INVALID_CODE');
    } else {
      this.error = this.translate.instant('AUTH.VERIFY_2FA.VERIFICATION_FAILED');
    }
    this.resetCodeInputs();
  }

  private onVerifyFailed(error: unknown): void {
    this.isVerifying = false;
    this.handleVerificationError(error);
    this.resetCodeInputs();
  }

  /**
   * For hotel users (extranet users / hotel managers), clear the saved hotel selection
   * so the hotel search screen is always shown after login.
   * Internal HotelCard staff are not affected.
   */
  private clearSavedHotelForHotelUser(): void {
    const user = this.userService.getUser();
    if (!user?.id || user.is_internal) {
      return;
    }
    localStorage.removeItem(`hc_selected_hotel_${user.id}`);
    localStorage.removeItem('lastHotelId');
  }

  /**
   * Navigates back to the login page and clears the temp token.
   */
  backToLogin(): void {
    this.userService.clearPendingTwoFactorState();
    this.router.navigate(['/account/login']);
  }

  private handleVerificationError(error: unknown): void {
    const status = this.getErrorStatus(error);

    if (status === 422) {
      this.error = this.translate.instant('AUTH.VERIFY_2FA.INVALID_CODE');
      return;
    }

    if (status === 401) {
      this.userService.clearPendingTwoFactorState();
      this.router.navigate(['/account/login'], {
        queryParams: { reason: 'two_factor_expired' },
      });
      return;
    }

    // The backend caps failed codes per user (the limit is backend config)
    // and then rejects correct codes too, so retrying right away cannot help.
    if (status === 429) {
      this.error = this.translate.instant('AUTH.VERIFY_2FA.TOO_MANY_ATTEMPTS');
      return;
    }

    this.error = this.translate.instant('AUTH.VERIFY_2FA.VERIFICATION_FAILED');
  }

  private getErrorStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }

  /**
   * Resets the code input fields.
   */
  private resetCodeInputs(): void {
    this.verificationDigits = ['', '', '', '', '', ''];
    this.recoveryCode = '';

    // Focus first digit input after reset
    if (!this.isRecoveryMode) {
      setTimeout(() => {
        const firstInput = document.getElementById('verify-digit-0') as HTMLInputElement;
        firstInput?.focus();
      });
    }
  }
}
